import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchSettings, submitInquiry } from "../lib/api";
import Reveal from "../components/Reveal";

const PROJECT_TYPES = [
    "Narrative",
    "Commercial",
    "Short Film",
    "Documentary",
    "Music Video",
    "Other",
];

const Contact = () => {
    const [settings, setSettings] = useState(null);
    const [form, setForm] = useState({
        name: "",
        email: "",
        project_type: "",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    useEffect(() => {
        fetchSettings()
            .then(setSettings)
            .catch(() => {});
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setSending(true);
        try {
            await submitInquiry({
                ...form,
                project_type: form.project_type || null,
            });
            setSent(true);
            toast.success("Thank you. Your message has been sent.");
            setForm({ name: "", email: "", project_type: "", message: "" });
        } catch (err) {
            toast.error(
                err?.response?.data?.detail?.[0]?.msg ||
                    "Something went wrong. Please try again.",
            );
        } finally {
            setSending(false);
        }
    };

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    return (
        <main
            data-testid="contact-page"
            className="pt-32 sm:pt-40 pb-32 mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14"
        >
            <Reveal>
                <p className="text-label text-muted-foreground mb-6">Contact</p>
                <h1 className="font-serif-display text-5xl sm:text-7xl tracking-tighter leading-[1.02] max-w-4xl">
                    Looking to <span className="italic text-muted-foreground">collaborate?</span>
                </h1>
                <p className="mt-8 max-w-xl text-lg text-foreground/75 leading-relaxed">
                    Narrative, commercial, documentary — if you are working on
                    something with intention, please write.
                </p>
            </Reveal>

            <div className="mt-20 grid lg:grid-cols-12 gap-16">
                <Reveal delay={100} className="lg:col-span-5">
                    <p className="text-label text-muted-foreground mb-4">
                        Direct
                    </p>
                    <ul className="space-y-6 text-base">
                        <li>
                            <p className="text-label text-muted-foreground mb-1">
                                Email
                            </p>
                            <a
                                href={`mailto:${settings?.contact_email || "hello@johnalexanderjimenez.com"}`}
                                data-testid="contact-email-link"
                                className="font-serif-display text-2xl underline-anim"
                            >
                                {settings?.contact_email ||
                                    "hello@johnalexanderjimenez.com"}
                            </a>
                        </li>
                        <li>
                            <p className="text-label text-muted-foreground mb-1">
                                Instagram
                            </p>
                            <a
                                href={
                                    settings?.instagram_url ||
                                    "https://instagram.com/"
                                }
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-instagram-link"
                                className="font-serif-display text-2xl underline-anim"
                            >
                                @johnalexanderjimenez
                            </a>
                        </li>
                        <li>
                            <p className="text-label text-muted-foreground mb-1">
                                Threads
                            </p>
                            <a
                                href={
                                    settings?.threads_url ||
                                    "https://www.threads.com/@j_alexander_jimenez"
                                }
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-threads-link"
                                className="font-serif-display text-2xl underline-anim"
                            >
                                @j_alexander_jimenez
                            </a>
                        </li>
                        <li>
                            <p className="text-label text-muted-foreground mb-1">
                                IMDb
                            </p>
                            <a
                                href={settings?.imdb_url || "https://www.imdb.com/"}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-imdb-link"
                                className="font-serif-display text-2xl underline-anim"
                            >
                                Filmography
                            </a>
                        </li>
                    </ul>
                </Reveal>

                <Reveal delay={200} className="lg:col-span-7">
                    {sent ? (
                        <div
                            data-testid="contact-success"
                            className="border border-border p-10 sm:p-14"
                        >
                            <p className="text-label text-muted-foreground mb-4">
                                Message sent
                            </p>
                            <p className="font-serif-display text-3xl leading-snug">
                                Thank you. John will be in touch shortly.
                            </p>
                            <button
                                type="button"
                                onClick={() => setSent(false)}
                                data-testid="contact-send-another"
                                className="mt-8 text-label text-muted-foreground underline-anim hover:text-foreground"
                            >
                                Send another message
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="space-y-8"
                            noValidate
                        >
                            <div className="grid sm:grid-cols-2 gap-6">
                                <label className="block">
                                    <span className="text-label text-muted-foreground">
                                        Name
                                    </span>
                                    <input
                                        type="text"
                                        value={form.name}
                                        onChange={update("name")}
                                        data-testid="contact-input-name"
                                        className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 text-base transition-colors"
                                        required
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-label text-muted-foreground">
                                        Email
                                    </span>
                                    <input
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        data-testid="contact-input-email"
                                        className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 text-base transition-colors"
                                        required
                                    />
                                </label>
                            </div>

                            <label className="block">
                                <span className="text-label text-muted-foreground">
                                    Project Type
                                </span>
                                <select
                                    value={form.project_type}
                                    onChange={update("project_type")}
                                    data-testid="contact-select-type"
                                    className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 text-base appearance-none transition-colors"
                                >
                                    <option value="" className="bg-background">
                                        Select project type
                                    </option>
                                    {PROJECT_TYPES.map((t) => (
                                        <option
                                            key={t}
                                            value={t}
                                            className="bg-background"
                                        >
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className="block">
                                <span className="text-label text-muted-foreground">
                                    Message
                                </span>
                                <textarea
                                    rows={6}
                                    value={form.message}
                                    onChange={update("message")}
                                    data-testid="contact-input-message"
                                    className="mt-3 w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 text-base resize-none transition-colors"
                                    required
                                />
                            </label>

                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="contact-submit"
                                className="bg-foreground text-background px-8 py-4 text-label hover:bg-foreground/90 transition-colors disabled:opacity-50"
                            >
                                {sending ? "Sending…" : "Send Message"}
                            </button>
                        </form>
                    )}
                </Reveal>
            </div>

            {/* Closing plaque — quiet, museum-label energy */}
            <Reveal delay={300}>
                <p
                    data-testid="contact-location"
                    className="mt-32 sm:mt-40 text-center font-serif-display italic text-base sm:text-lg text-foreground/55 tracking-tight"
                >
                    Based in Sacramento. Available worldwide.
                </p>
            </Reveal>
        </main>
    );
};

export default Contact;
